import React, { FormEvent, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import { AuthHelper, AuthToken, MfaSmsChallenge, MfaVerifyPhoneChallenge } from '../../helpers/AuthHelper';
import { Spinner } from 'react-bootstrap';
import './MfaAuthForm.scss';

interface MfaAuthFormProps {
  mfaChallenge: MfaSmsChallenge|MfaVerifyPhoneChallenge;
  onCompleted: (authToken?: AuthToken) => void;
  onReturn: () => void;
}

export function MfaAuthForm({ mfaChallenge, onCompleted, onReturn }: MfaAuthFormProps): JSX.Element {
  const [mfaKey, setMfaKey] = useState('');
  const [trustDevice, setTrustDevice] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string|null>(null);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const isLoginSmsChallenge = mfaChallenge instanceof MfaSmsChallenge;
  const submitText = isLoginSmsChallenge ? 'Login' : 'Afronden';

  const handleSubmitSmsChallenge = (mfaSmsChallenge: MfaSmsChallenge): void => {
    setSubmitError(null);
    setSubmitLoading(true);
    AuthHelper.handleMfaSmsChallenge(mfaKey, mfaSmsChallenge)
      .then((authToken) => {
        if (trustDevice) {
          AuthHelper.trustDevice(mfaSmsChallenge.cognitoUser)
            .catch(err => console.error('Failed to trust device', err));
        }
        onCompleted(authToken);
      })
      .catch(err => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        switch (err.code) {
          case 'NotAuthorizedException':
          case 'ExpiredCodeException':
            setSubmitError('Code is verlopen, probeer het opnieuw met een nieuwe code.');
            break;
          case 'TooManyRequestsException':
            setSubmitError('Er zijn te veel pogingen gedaan in een korte periode. Probeer het later opnieuw.');
            break;
          case 'CodeMismatchException':
            setSubmitError('Ongeldige code ingevoerd. Controleer de ingevoerde code.');
            break;
          default:
            setSubmitError('Autorisatie mislukt door een onverwachte fout');
            console.error('Unexpected mfa challenge response:', err);
            break;
        }
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  const handleSubmitVerifyPhoneChallenge = (mfaVerifyPhoneChallenge: MfaVerifyPhoneChallenge): void => {
    setSubmitError(null);
    setSubmitLoading(true);
    AuthHelper.handleMfaVerifyPhoneChallenge(mfaKey, mfaVerifyPhoneChallenge)
      .then(() => {
        onCompleted(); // Return to log in again.
      })
      .catch(err => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        switch (err.code) {
          case 'NotAuthorizedException':
          case 'ExpiredCodeException':
            setSubmitError('Code is verlopen, probeer het opnieuw met een nieuwe code.');
            break;
          case 'TooManyRequestsException':
            setSubmitError('Er zijn te veel pogingen gedaan in een korte periode. Probeer het later opnieuw.');
            break;
          case 'CodeMismatchException':
            setSubmitError('Ongeldige code ingevoerd. Controleer de ingevoerde code.');
            break;
          default:
            setSubmitError('Autorisatie mislukt door een onverwachte fout');
            console.error('Unexpected mfa challenge response:', err);
            break;
        }
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();

    if (mfaChallenge instanceof MfaSmsChallenge) {
      handleSubmitSmsChallenge(mfaChallenge);
    } else if (mfaChallenge instanceof MfaVerifyPhoneChallenge) {
      handleSubmitVerifyPhoneChallenge(mfaChallenge);
    }
  };

  const handleReturn = (): void => {
    onReturn();
  };

  const handleInput = (value: string): void => {
    const clean = value.trim().replaceAll(/\D/g, '');
    setMfaKey(clean);
  };

  return <Form className="form" onSubmit={handleSubmit}>
    <Form.Group className="form__item">
      <Form.Label>SMS-Code</Form.Label>
      <Form.Control
        type="text"
        autoComplete="one-time-code"
        value={mfaKey}
        onChange={(event) => handleInput(event.target.value)}
      />
      <Form.Text className="text-muted">
        Code niet ontvangen? <button className="mfa-auth-form__no-code-button" type="button" onClick={handleReturn}>Log dan opnieuw in</button>.
      </Form.Text>
    </Form.Group>

    { isLoginSmsChallenge && <Form.Group>
      <Form.Check type="checkbox" id="trustDevice">
        <Form.Check.Input type="checkbox" checked={trustDevice} onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTrustDevice(event.currentTarget.checked)} />
        <Form.Check.Label>
          Dit apparaat vertrouwen.
        </Form.Check.Label>
        <Form.Text className="text-muted">
          Op een vertrouwd apparaat hoef je bij het inloggen geen SMS-Code meer in te vullen.
        </Form.Text>
      </Form.Check>
    </Form.Group> }


    { submitError && <Alert variant="danger">{submitError}</Alert> }
    <Button type="submit" className="form__submit" variant="primary" disabled={mfaKey.length < 6 || submitLoading}>
      { submitLoading && <Spinner variant="primary" animation="border" /> }{submitText}
    </Button>
    <Button type="button" className="form__link" variant="link" onClick={handleReturn}>Ga terug</Button>
  </Form>;
}
